import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, X, ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = "smarthivesms_cookie_consent";

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Small delay for smooth entry
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for custom event to open cookie settings from footer
  useEffect(() => {
    const handleOpenSettings = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed.preferences) setPreferences(parsed.preferences);
        } catch {
          // fallback
        }
      }
      setIsVisible(true);
      setShowDetails(true);
    };

    window.addEventListener("open-cookie-settings", handleOpenSettings);
    return () => window.removeEventListener("open-cookie-settings", handleOpenSettings);
  }, []);

  const saveConsent = (status: "all" | "essential" | "custom", prefs?: CookiePreferences) => {
    const finalPrefs: CookiePreferences =
      status === "all"
        ? { essential: true, analytics: true, marketing: true }
        : status === "essential"
        ? { essential: true, analytics: false, marketing: false }
        : prefs || preferences;

    const consentData = {
      status,
      timestamp: new Date().toISOString(),
      preferences: finalPrefs,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-4 right-4 left-4 md:left-auto md:max-w-md z-50"
      >
        <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-2xl p-5 shadow-2xl border border-gray-200/80 dark:border-slate-800 text-slate-800 dark:text-slate-100">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-[#0F4145]/10 text-[#0F4145] dark:bg-emerald-950/60 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                  We Value Your Privacy
                </h3>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  GDPR & NDPR Compliant
                </span>
              </div>
            </div>
            <button
              onClick={() => saveConsent("essential")}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg transition-colors"
              title="Close & Accept Essential"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Description */}
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
            We use cookies to enhance your experience, analyze SMS routing performance, and deliver personalized developer tools. Read our{" "}
            <Link
              to="/privacy-policy"
              className="text-[#0F4145] dark:text-emerald-400 font-semibold underline hover:opacity-80"
              onClick={() => setIsVisible(false)}
            >
              Privacy Policy
            </Link>.
          </p>

          {/* Expandable Preferences */}
          {showDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-2 mb-4 pt-2 border-t border-slate-100 dark:border-slate-800"
            >
              {/* Essential */}
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-800/60">
                <div className="pr-2">
                  <p className="text-xs font-semibold text-slate-900 dark:text-white">Essential Cookies</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Required for authentication and platform security.</p>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400">
                  Always Active
                </span>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-800/60">
                <div className="pr-2">
                  <p className="text-xs font-semibold text-slate-900 dark:text-white">Analytics & Performance</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Helps us optimize messaging latency & telemetry.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-8 h-4 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#0F4145]"></div>
                </label>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-800/60">
                <div className="pr-2">
                  <p className="text-xs font-semibold text-slate-900 dark:text-white">Marketing & Updates</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Relevant announcements & developer features.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-8 h-4 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#0F4145]"></div>
                </label>
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => saveConsent("all")}
                className="w-full py-2 px-3 rounded-xl bg-[#0F4145] hover:bg-[#0F4145]/90 text-white font-bold text-xs shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <Check className="w-3.5 h-3.5" />
                Accept All
              </button>
              <button
                type="button"
                onClick={() => saveConsent("essential")}
                className="w-full py-2 px-3 rounded-xl border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold text-xs text-slate-700 dark:text-slate-200 transition-all text-center cursor-pointer"
              >
                Essential Only
              </button>
            </div>

            {showDetails ? (
              <button
                type="button"
                onClick={() => saveConsent("custom", preferences)}
                className="w-full py-1.5 text-center text-[11px] font-bold text-[#0F4145] dark:text-emerald-400 hover:underline cursor-pointer"
              >
                Save My Preferences
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="w-full py-1 text-center text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 flex items-center justify-center gap-1 cursor-pointer"
              >
                <span>Customize Preferences</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
